import React from 'react';
import { motion } from 'framer-motion';
import { Truck, Shield, Zap, ArrowRight, CheckCircle2, MapPin, FileCheck } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsTrucking({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-[#0F172A] text-white font-sans" data-id="element-2217">
      <SimonsHeader activePage="trucking" setActivePage={setActivePage} data-id="element-2218" />

      {/* Hero Section */}
      <section className="py-32 px-8 relative overflow-hidden" data-id="element-2219">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-20 items-center relative z-10" data-id="element-2220">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-2221">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FFD700]/10 border border-[#FFD700]/20 rounded-full text-[#FFD700] text-[10px] font-black uppercase tracking-widest mb-8" data-id="element-2222">
              Operational Continuity Guaranteed
            </div>
            <h1 className="text-6xl lg:text-8xl font-black mb-8 leading-[0.9] tracking-tighter" data-id="element-2223">
              Keep Your <br data-id="element-2224" />
              <span className="text-[#D32F2F]" data-id="element-2225">Fleet Moving.</span>
            </h1>
            <p className="text-xl text-white/60 mb-10 max-w-xl font-medium leading-relaxed" data-id="element-2226">
              Fast-track compliance and insurance for the logistics industry. We
              understand that every hour off the road is revenue lost.
            </p>
            <div className="flex flex-wrap gap-6" data-id="element-2227">
              <div className="flex items-center gap-3" data-id="element-2228">
                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-[#FFD700] border border-white/10" data-id="element-2229">
                  <Zap size={20} data-id="element-2230" />
                </div>
                <span className="text-sm font-bold" data-id="element-2231">
                  2-Hour Quote Turnaround
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-2232">
                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center text-[#FFD700] border border-white/10" data-id="element-2233">
                  <FileCheck size={20} data-id="element-2234" />
                </div>
                <span className="text-sm font-bold" data-id="element-2235">DOT Compliance Ready</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-[32px] p-10 text-[#002366] shadow-2xl relative" data-id="element-2236">
            <h3 className="text-2xl font-black mb-8" data-id="element-2237">Quick Quote</h3>
            <form onSubmit={e => {
            e.preventDefault();
            setActivePage('quote');
          }} className="space-y-6" data-id="element-2238">
              <div className="space-y-2" data-id="element-2239">
                <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-2240">
                  DOT Number
                </label>
                <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366] font-bold" placeholder="1234567" data-id="element-2241" />
              </div>
              <div className="space-y-2" data-id="element-2242">
                <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-2243">
                  Fleet Size
                </label>
                <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366] font-bold" data-id="element-2244">
                  <option data-id="element-2245">Owner-Operator (1 Truck)</option>
                  <option data-id="element-2246">Small Fleet (2-10 Trucks)</option>
                  <option data-id="element-2247">Large Fleet (10+ Trucks)</option>
                </select>
              </div>
              <button type="submit" className="w-full py-5 bg-[#D32F2F] text-white rounded-xl font-black uppercase tracking-widest hover:bg-[#B71C1C] transition-all shadow-xl shadow-[#D32F2F]/20" data-id="element-2248">
                Get Quote Now
              </button>
            </form>
          </motion.div>
        </div>
        <div className="absolute top-0 right-0 w-full h-full bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#D32F2F]/10 -z-10" data-id="element-2249"></div>
      </section>

      {/* Fleet Types */}
      <section className="py-24 px-8 max-w-7xl mx-auto" data-id="element-2250">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2251">
          {[{
          title: 'Full Fleet',
          icon: Truck,
          desc: 'Comprehensive liability and physical damage for multi-unit operations.'
        }, {
          title: 'Box Trucks',
          icon: MapPin,
          desc: 'Specialized local delivery and last-mile logistics protection.'
        }, {
          title: 'Owner-Operators',
          icon: Shield,
          desc: 'Flexible coverage designed for the independent professional.'
        }].map((item, i) => <div key={i} className="p-10 bg-white/5 rounded-[24px] border border-white/10 hover:border-[#FFD700] transition-all group" data-id="element-2252">
              <div className="w-16 h-16 bg-white/5 rounded-2xl flex items-center justify-center text-[#FFD700] mb-8 border border-white/10 group-hover:bg-[#FFD700] group-hover:text-[#002366] transition-all" data-id="element-2253">
                <item.icon size={32} data-id="element-2254" />
              </div>
              <h3 className="text-2xl font-black mb-4" data-id="element-2255">{item.title}</h3>
              <p className="text-white/40 font-medium leading-relaxed mb-6" data-id="element-2256">
                {item.desc}
              </p>
              <div className="flex items-center gap-2 text-[#FFD700] font-bold text-sm" data-id="element-2257">
                <CheckCircle2 size={16} data-id="element-2258" /> Operational Continuity
              </div>
            </div>)}
        </motion.div>
      </section>

      <SimonsFooter data-id="element-2259" />
    </div>;
}